// Phase G4-B — real useEngineQuestions.
//
// Replaces the G3 mock. Runs the real adaptation engine:
//   1. buildDecisionInput(childId, skillId) → decideNextActivity
//   2. WALKBACK TRANSLATION (§G4-Q4): if the decision is "walkback",
//      pull items for `remediationSkillId` (the prereq), NOT the
//      original skill — the session screen is unaffected, it just
//      renders whatever items come back.
//   3. Pull N launch_safe items for the resolved skill via the
//      fail-closed content reader and map to EngineItem[].
//
// Signature widened from the mock's (microSkillId, difficulty, count)
// to (skillId, childId, count): the engine needs the childId to read
// the kid's attempt history. MathSession (the only consumer) is
// updated in G4-C. When childId is null (no active kid yet), the hook
// skips the decision and just serves launch_safe items for the skill
// so the screen still renders.

import { useQuery } from "@tanstack/react-query";
import { qk } from "@/lib/engine/queryKeys";
import type { EngineItem } from "@/types/engine";
import type { AdaptationDecision, ContentAudience, ChildAttempt } from "@/adaptation/types";
import { getContentForMicroSkill, getMergedContentForMicroSkill } from "@/curriculum/readers/contentReader";
import { decideNextActivity } from "@/adaptation/adaptationDecisionEngine";
import { isReadyForTransferForceServe } from "@/adaptation/transferGating";
import { buildDecisionInput } from "./buildDecisionInput";
import { primaryPackForSkill, PACK_MERGE } from "./packRouting";
import {
  contentItemToEngineItem,
  type MappedEngineItem,
} from "./contentToEngineItem";
import { withBoundedRepetition } from "./boundedRepetition";
import { seededShuffle, localDayEpoch } from "./seededShuffle";
import { spreadRepeatedPictures } from "./spreadRepeatedPictures";
import { isDevBypass } from "@/lib/persistence";
import { addContentPoolBreadcrumb, addWalkbackFallbackBreadcrumb } from "@/lib/sentry";

/** RC-F — pack-aware contentFile for evidence rows. The session screens
 * historically hardcoded their strand's pack path (Reading:
 * "controlledSentences.json" even when the served item came from
 * encodingWords via the ms.read.08 PACK_OVERRIDE). Derive it from the
 * SAME routing the item pull used so the attempt record names the file
 * the item actually came from. Pass the ITEM's microSkillId (the
 * post-walkback resolved skill the item was pulled for), not the
 * question's skillId. Pure (no React) — callable from tests. */
export function contentFileForSkill(skillId: string): string {
  return `src/curriculum/data/content/${primaryPackForSkill(skillId)}.json`;
}

/** MA-663 round 2 (⭐M4) — the per-ITEM counterpart to contentFileForSkill,
 *  for a served EngineItem that may carry `sourcePack` (PACK_MERGE skills
 *  only — useEngineQuestions.ts's pullItems stamps it on every merged
 *  record). Falls back to contentFileForSkill(skillId) when `sourcePack`
 *  is absent (every single-pack skill; byte-identical to the pre-existing
 *  call sites). Session screens should call THIS at attempt-log time
 *  instead of contentFileForSkill directly whenever the served item is
 *  available — for ms.math.03 the two answers diverge for exactly the 60
 *  foundationItems records (contentFileForSkill alone would stamp every
 *  attempt "mathExemplars.json", including the ~47% that were actually
 *  pulled from foundationItems.json). */
export function contentFileForItem(
  skillId: string,
  item: Pick<MappedEngineItem, "sourcePack"> | null | undefined,
): string {
  const pack = item?.sourcePack ?? primaryPackForSkill(skillId);
  return `src/curriculum/data/content/${pack}.json`;
}

/** MA-303b RED round 2 (S5) — module-level named itemFilter (per MA-149's
 *  "referentially-stable predicate" contract) that excludes any authored
 *  transfer probe from a pool. Used ONLY for the review-pool fetch in
 *  `useEngineQuestionsForRun` — see that call site's comment. Exported for
 *  the behavioural pin (asserting the real function is wired in, not a
 *  re-implementation). */
export function excludeTransferProbes(item: MappedEngineItem): boolean {
  return item.isTransferProbe !== true;
}

/** MA-869 (D-1013-11) — whether `pullItems` drops its "content pool short"
 *  breadcrumb: only when repeats were served and the caller is not quiet. */
export function shouldEmitPoolBreadcrumb(duplicatesServed: number, quiet?: boolean): boolean {
  return duplicatesServed > 0 && quiet !== true;
}

/** Pull up to `count` items for a skill, mapped to EngineItem.
 * Audience defaults to independent_practice (fail-closed: launch_safe
 * only). Foundation practice passes "scaffolded" — the scaffold (guide
 * letter / pad outline / tiles) is the safety surface, so the system
 * serves expert_review_needed foundation content there without flipping
 * it to launch_safe (contentReader semantics).
 * I.4.5: export pullItems with the remediation-switch consumer
 * MA-663 round 2 (⭐M3) — also exported for scripts/audit/
 * packMergeReachabilityGuard.mjs, which must drive the REAL serve path
 * (not re-derive its own reader call) to make its "reachable through
 * pullItems" claim true rather than asserted. */
export function pullItems(
  skillId: string,
  count: number,
  audience: ContentAudience,
  // ENGINE-VARIETY: when a kid id is supplied (the real serving path), the
  // unique pool is shuffled by a (childId, skillId, local-day) seed so the
  // kid doesn't meet the SAME authored items in the SAME order every session
  // (the "repeating questions" report). Omitted (null) on the deterministic
  // count path (servedItemCountForSkill) — the COUNT is shuffle-invariant, and
  // the trail/focal gates must stay deterministic.
  childId: string | null = null,
  // ENGINE-VARIETY: a per-SESSION token (the screen passes a fresh value each
  // mount) so the serving order re-randomizes on every session start — the
  // "non-linear, changes every time you start" ask. When omitted, falls back
  // to the local-day bucket (rotates daily, stable within a day).
  sessionSeed?: string,
  // MA-149: an optional surface-specific servability predicate, applied to
  // the UNIQUE pool before withBoundedRepetition sees it. Exists so a
  // caller like MixedSession can exclude items its screen can't render
  // (e.g. visualRequired math items — MixedSession mounts no HintSlot) at
  // the SAME code path servedItemCountForSkill counts with, so a skill's
  // reported pool size and its actually-served items can never disagree
  // (the MA-163 desync class). Omitted = every item counts, prior behavior.
  itemFilter?: (item: MappedEngineItem) => boolean,
  // MA-303 (registry-flip, OPERATOR DECISION D3) — an optional predicate
  // identifying item(s) that MUST survive shuffle-then-truncate into the
  // returned pool whenever they are present after `itemFilter`. Applied
  // AFTER the seeded shuffle (so force-served content still rotates which
  // OTHER items fill the rest of the pool) and BEFORE withBoundedRepetition
  // (so it survives the count-based truncation that would otherwise drop a
  // low-difficulty-rank item competing for a slot against ~10+ ordinary
  // items — the exact reachability gap the 2026-07-26 BACKLOG decision log
  // named). Matched items are moved to the front of the pool, guaranteeing
  // inclusion in withBoundedRepetition's round-0 slice as long as
  // `count >= ` the number of matched items (true for every real session
  // length). Order-preserving among both the forced and non-forced
  // remainders. Omitted = no forcing, prior behavior unchanged byte-for-
  // byte (every skill outside the force-serve gate).
  forceInclude?: (item: MappedEngineItem) => boolean,
  // MA-869 (D-1013-11) — `quiet: true` suppresses the "content pool short"
  // breadcrumb for a caller that reads the whole pool on purpose (the parent
  // skill page's example). Omitted = the breadcrumb rule is unchanged for
  // every other caller (shouldEmitPoolBreadcrumb pins it).
  quiet?: boolean,
): MappedEngineItem[] {
  // MA-663 — a skill in PACK_MERGE serves the UNION of every listed pack,
  // each record dispatched by ITS OWN source pack (never skillId's
  // primaryPackForSkill), so a foundationItems record never reaches
  // mathExemplarToEngineItem and vice versa. Every other skill takes the
  // unchanged single-pack path below.
  const merge = PACK_MERGE[skillId];
  // Map the FULL unique pool (not a pre-slice): withBoundedRepetition
  // needs to see every available item to bound repeats correctly AND to
  // report the true unique count in the breadcrumb. Its round 0 returns
  // the first `count` items in order, so for any adequately-resourced
  // skill (uniques >= count, the norm) this is identical to the old
  // slice-then-map — no behaviour change. Only under-resourced skills
  // (#27: ms.math.05=1, ms.math.07=2) diverge, where the cap is exactly
  // what we want.
  let uniquePool: MappedEngineItem[];
  if (merge) {
    const merged = getMergedContentForMicroSkill(skillId, merge, { audience });
    // MA-663 round 2 (⭐M4) — stamp sourcePack on every merged record so a
    // consumer that needs the item's REAL pack (attempt-record attribution
    // — contentFileForSkill's per-item counterpart, contentFileForItem)
    // doesn't have to re-derive it from primaryPackForSkill, which for a
    // PACK_MERGE skill answers only ONE of the packs actually served.
    uniquePool = merged.map(({ pack: recordPack, item }) => ({
      ...contentItemToEngineItem(recordPack, item as unknown as Record<string, unknown>, skillId),
      sourcePack: recordPack,
    }));
  } else {
    const pack = primaryPackForSkill(skillId);
    const raw = getContentForMicroSkill(skillId, pack, { audience }) as Record<string, unknown>[];
    uniquePool = raw.map((item) => contentItemToEngineItem(pack, item, skillId));
  }
  if (itemFilter) uniquePool = uniquePool.filter(itemFilter);
  // ENGINE-VARIETY seeded shuffle. Gated OFF under the dev/e2e bypass so the
  // committed visual-snapshot baselines (which navigate with ?dev=1 and assert
  // the rendered item) stay byte-identical. The seed is stable for the local
  // day, so the order is fixed for a whole session (no mid-session reshuffle on
  // a React-Query refetch) and rotates daily + per-kid. The difficulty-driven
  // selection (pickItemForTier) is unchanged — this only varies WHICH item
  // among equal-difficulty candidates and the within-tier order.
  if (childId && !isDevBypass()) {
    const variety = sessionSeed ?? String(localDayEpoch());
    uniquePool = seededShuffle(uniquePool, `${childId}|${skillId}|${variety}`);
  }
  // MA-687 (RED round 1 MUST-1) — a merged pool can hold many records that
  // draw the same picture with the same answer (ms.math.05: 80 records, 13
  // bonds). Order every distinct picture before any repeat; the pool is the
  // same set, only the order moves (spreadRepeatedPictures.ts). Single-pack
  // pools are untouched (byte-identical order).
  if (merge) uniquePool = spreadRepeatedPictures(uniquePool);
  // MA-303 (D3) — move any force-included item(s) to the FRONT of the pool
  // so they survive withBoundedRepetition's count-based truncation
  // regardless of shuffle position. Order-only within each group; does not
  // widen the pool, does not touch MAX_REPS_PER_ITEM, does not change
  // `count`. A no-op (byte-identical uniquePool order) when forceInclude is
  // omitted or matches nothing — every non-registry skill is unaffected.
  if (forceInclude) {
    const forced: MappedEngineItem[] = [];
    const rest: MappedEngineItem[] = [];
    for (const it of uniquePool) {
      (forceInclude(it) ? forced : rest).push(it);
    }
    if (forced.length > 0) uniquePool = [...forced, ...rest];
  }
  const { items, uniqueItemsServed, duplicatesServed } = withBoundedRepetition(
    uniquePool,
    count,
    (it) => ({ ...it, isRepetition: true }),
  );
  if (shouldEmitPoolBreadcrumb(duplicatesServed, quiet)) {
    addContentPoolBreadcrumb({
      moduleId: skillId,
      requestedCount: count,
      uniqueItemsServed,
      duplicatesServed,
    });
  }
  return items;
}

/**
 * PR-AGE-MODEL — how many items a strand would ACTUALLY serve for `skillId`
 * at a requested length, AFTER the #27 bounded-repetition cap. This is the
 * SAME pool path `pullItems` runs (primaryPackForSkill → getContentForMicroSkill
 * → withBoundedRepetition), so the number it returns equals what a real session
 * renders — `effectiveLength = min(sessionLength, items.length)`.
 *
 * Parent settings calls this to surface the EFFECTIVE served length next to the
 * age-CONFIGURED length, making content-pool starvation visible (the real cause
 * of short sessions on under-resourced skills — Phase-H content authoring, not
 * the age model). Returns 0 when the skill resolves to no launch-safe content;
 * the caller treats 0 as "unknown" and shows no fabricated number.
 *
 * Pure (no React/query) so it can run in a render or a unit test.
 */
export function servedItemCountForSkill(
  skillId: string,
  requestedCount: number,
  audience: ContentAudience = "independent_practice",
  // MA-149: forwarded to pullItems — see its doc comment. Lets a caller ask
  // "how many items would this skill ACTUALLY serve on my surface" instead
  // of the surface-blind raw count.
  itemFilter?: (item: MappedEngineItem) => boolean,
): number {
  if (!skillId || requestedCount <= 0) return 0;
  return pullItems(skillId, requestedCount, audience, null, undefined, itemFilter).length;
}

interface EngineQuestionsResult {
  /** MappedEngineItem extends EngineItem with the source item's
   *  launchStatus, so the session screen can stamp the REAL
   *  launchStatus on the attempt record (G4-C watch item) instead of
   *  buildAttempt's launch_safe default. */
  items: MappedEngineItem[];
  /** I.2.c — the two un-zipped, already-bounded pools (focalResult.items /
   *  reviewResult.items) so the run-server can re-order within them.
   *  Absent on useEngineQuestions (single-skill); present on …ForRun. */
  focalPool?: MappedEngineItem[];
  reviewPool?: MappedEngineItem[];
  /** The resolved skill (post-walkback-translation) the items belong
   *  to — may differ from the requested skillId when the engine routed
   *  to a walkback prereq. */
  resolvedSkillId: string;
  /** The engine decision that produced this item set (null when no
   *  childId, i.e. the no-engine fallback path). */
  decision: AdaptationDecision | null;
  loading: boolean;
  error: Error | null;
  /** Prior attempts for this skill fetched at mount.  Populated when
   *  childId is non-null (engine path).  Used by session screens to seed
   *  sessionAttemptsRef for decideRuntimeResponse's in-memory accumulator.
   *  FD-4: Option B (widen EngineQuestionsResult). */
  priorAttempts?: ChildAttempt[];
}

/** Which skill's items to actually serve given the engine decision. A
 *  "walkback" decision (e.g. a same-error loop) serves the prerequisite
 *  (remediationSkillId); every other decision serves the requested skill.
 *
 *  MA-306: `isRemediationServable`, when supplied, gates the substitution —
 *  a walkback decision whose remediationSkillId has an EMPTY pool under the
 *  audience actually being queried is REFUSED, falling back to
 *  requestedSkillId instead. requestedSkillId reaches this function only
 *  after passing the assign-time / mission-pick servability gates
 *  (assignedSkills.ts's nextAssignedFocalSkill, useTodaysMission's chapter
 *  pick), so it is always already known-servable — the fallback can never
 *  itself produce an empty session. Without this guard, a routed skill's
 *  walkback target can be an unrouted, permanently-scaffolded-only
 *  prerequisite (e.g. ms.read.06 → ms.read.05, ms.read.09 → ms.read.07 —
 *  see ADR-055) with a zero independent_practice pool, silently stranding
 *  the kid on SessionLoadFallback's "No questions ready here yet" dead end.
 *  Omitted (the 2-arg call, e.g. existing tests / callers) preserves the
 *  prior unconditional-substitute behavior.
 *  Pure + exported for unit test. */
export function resolveServedSkillId(
  decision: Pick<AdaptationDecision, "decision" | "remediationSkillId">,
  requestedSkillId: string,
  isRemediationServable?: (skillId: string) => boolean,
): string {
  if (
    decision.decision === "walkback" &&
    decision.remediationSkillId &&
    (!isRemediationServable || isRemediationServable(decision.remediationSkillId))
  ) {
    return decision.remediationSkillId;
  }
  return requestedSkillId;
}

/** MA-306 — the real (childId present) engine resolution path, extracted
 *  from useEngineQuestions' queryFn so the FULL chain (buildDecisionInput →
 *  decideNextActivity → resolveServedSkillId's servability guard →
 *  pullItems) is directly unit-testable end-to-end, without a React Query
 *  harness. useEngineQuestions is the only production caller — this exists
 *  so a wiring bug at the call site (wrong count/audience, forgotten
 *  servability arg) is caught by the same test that proves the fix, rather
 *  than only exercising resolveServedSkillId in isolation. Pure async,
 *  exported for unit test. */
export async function resolveEngineQuestions(
  childId: string,
  skillId: string,
  count: number,
  audience: ContentAudience,
  sessionSeed?: string,
  itemFilter?: (item: MappedEngineItem) => boolean,
  // MA-303b RED round 1 (R2) — OPT-IN per call site, default false. RED
  // found MixedSession reached the force-serve gate through this SAME
  // resolveEngineQuestions path (via useEngineQuestions) despite never
  // tagging attempts (transferTagsForItem's only production call site is
  // MathSession.tsx) — a child could meet the sole authored probe on the
  // Mixed screen, answer it, and have the evidence silently thrown away
  // forever (isReadyForTransferForceServe would keep returning true next
  // session too, since no tagged attempt was ever written). Only a caller
  // that ALSO tags (MathSession's FOCAL fetch) may pass true.
  allowTransferForceServe = false,
  // MA-303b RED round 2 (S2/S3/S6) — optional injectable client, forwarded
  // to buildDecisionInput. Test-only seam (production's useEngineQuestions
  // never passes it, so every real call is unaffected); exists so an R2
  // pin can exercise THIS function's real force-gate wiring against a
  // stub data source instead of re-implementing the gate expression.
  client?: Parameters<typeof buildDecisionInput>[2],
): Promise<{
  items: MappedEngineItem[];
  resolvedSkillId: string;
  decision: AdaptationDecision;
  priorAttempts: ChildAttempt[];
}> {
  const input = await buildDecisionInput(childId, skillId, client);
  const decision = decideNextActivity(input);

  // MA-306: the servability check uses the EXACT SAME count/audience/
  // itemFilter that pullItems is about to be called with below — no drift
  // possible between "checked servable" and "actually served."
  const resolvedSkillId = resolveServedSkillId(
    decision,
    skillId,
    (remediationSkillId) => servedItemCountForSkill(remediationSkillId, count, audience, itemFilter) > 0,
  );

  if (
    decision.decision === "walkback" &&
    decision.remediationSkillId &&
    resolvedSkillId !== decision.remediationSkillId
  ) {
    addWalkbackFallbackBreadcrumb({
      requestedSkillId: skillId,
      emptyRemediationSkillId: decision.remediationSkillId,
      servedSkillId: resolvedSkillId,
    });
  }

  // MA-303 (registry-flip, D3) — force-serve gate. `input` was built for
  // the REQUESTED skillId, so its masteryState/transferEvidence are only
  // valid for that same skill; only compute a force when no walkback
  // substitution moved serving onto a different skill (resolvedSkillId ===
  // skillId — the common, non-remediation case, and the only one this
  // slice's registry (ms.math.09) needs). A walked-back session (serving a
  // prerequisite) never forces — the prerequisite is never itself a
  // registry-servable skill in practice, and forcing off mismatched
  // evidence would be unsound. R2 (RED round 1) — ALSO gated on the
  // caller's own opt-in; a caller that does not tag must never force.
  const forceInclude =
    allowTransferForceServe &&
    resolvedSkillId === skillId &&
    isReadyForTransferForceServe(
      skillId,
      input.attemptWindow.attempts,
      input.masteryState.provisionalMastery,
    )
      ? (item: MappedEngineItem) => item.isTransferProbe === true
      : undefined;

  return {
    items: pullItems(resolvedSkillId, count, audience, childId, sessionSeed, itemFilter, forceInclude),
    resolvedSkillId,
    decision,
    priorAttempts: input.attemptWindow.attempts,
  };
}

export function useEngineQuestions(
  skillId: string,
  childId: string | null,
  count: number,
  audience: ContentAudience = "independent_practice",
  // ENGINE-VARIETY: per-session seed (threaded from the session screen) so the
  // served order varies on every session start. Part of the queryKey so a new
  // session (new seed) refetches + reshuffles; stable within a mount.
  sessionSeed?: string,
  // MA-149: forwarded to pullItems — see its doc comment. The caller is
  // responsible for passing a referentially-stable predicate (a module-level
  // named function, not an inline lambda) since it is not part of the
  // queryKey.
  itemFilter?: (item: MappedEngineItem) => boolean,
  // MA-303b RED round 1 (R2) — opt-in force-serve, default false; see
  // resolveEngineQuestions' doc comment. Round 2 (NIT 4): now PART OF the
  // queryKey — round 1 kept it out (reasoning: it's a caller-identity
  // property, not session variety), but RED's qk.mjs showed that when the
  // focal and review hooks share every other key component (reviewSkillId
  // === focalSkillId), the shared key made allow=true/false observers
  // collide, and whichever subscribed first silently won the cache
  // regardless of the other's flag. Callers still pass a stable boolean
  // literal, not a per-render value — this only widens the key, it does
  // not turn the flag into session variety.
  allowTransferForceServe = false,
): EngineQuestionsResult {
  const query = useQuery({
    queryKey: qk.engineQuestions.for(skillId, childId, count, audience, sessionSeed, allowTransferForceServe),
    queryFn: async (): Promise<{
      items: MappedEngineItem[];
      resolvedSkillId: string;
      decision: AdaptationDecision | null;
      priorAttempts: ChildAttempt[];
    }> => {
      // No active kid → serve items for the skill, no decision routing
      // (the screen still renders).
      if (!childId) {
        return {
          items: pullItems(skillId, count, audience, childId, sessionSeed, itemFilter),
          resolvedSkillId: skillId,
          decision: null,
          priorAttempts: [],        // no engine path → empty prior attempts
        };
      }

      // Real engine path (buildDecisionInput → decideNextActivity →
      // walkback-servability-gated resolveServedSkillId → pullItems).
      return resolveEngineQuestions(childId, skillId, count, audience, sessionSeed, itemFilter, allowTransferForceServe);
    },
    staleTime: 0,
  });

  return {
    items: query.data?.items ?? [],
    resolvedSkillId: query.data?.resolvedSkillId ?? skillId,
    decision: query.data?.decision ?? null,
    priorAttempts: query.data?.priorAttempts ?? [],
    loading: query.isLoading,
    error: (query.error as Error | null) ?? null,
  };
}

// ── T1.4c: SessionRun-aware item orchestration ────────────────────────

/** T1.4c — interleave-aware item orchestration. Given a SessionRun (from
 *  buildSessionRun) and a kid id, fetch items for the focal AND review
 *  pools and zip per the run's per-ordinal pattern.
 *
 *  Same-strand-only wiring contract (T1.4c): if a question's role is
 *  "cross_strand" with a skillId from the OTHER strand, this hook
 *  substitutes with the focal pool — because MathSession can't render
 *  reading items and vice versa. The full cross-strand renderer is
 *  deferred to a follow-up. The SessionRun shape stays truthful so
 *  parent-results can report the engine's actual pick; only the
 *  RENDERED items collapse.
 *
 *  Hooks discipline: always call useEngineQuestions for both focal and
 *  review pools regardless of run shape (when reviewSkillId is null,
 *  the review fetch falls back to the focal skill — harmless dup).
 *  This is the canonical fix for the conditional-hook prohibition.
 */
/** MA-303b RED round 2 (S2/S3/S6) — the review-pool call's (itemFilter,
 *  allowTransferForceServe) arguments, extracted to a pure named function
 *  so the WIRING (which the round-1 regex pin could only check as source
 *  text) is testable without a React/React-Query harness — this repo's
 *  test convention has none (no renderHook usage anywhere). useEngineQuestionsForRun's
 *  review call below is this function's only call site; it always spreads
 *  its return rather than re-deriving the decision inline, so a test
 *  calling this function directly observes the SAME values the real
 *  review call passes. Always `[excludeTransferProbes, false]` — the
 *  review pool never force-serves (independent of the caller's focal
 *  opt-in) and always excludes transfer probes (S5). */
export function reviewPoolCallArgs(): [
  itemFilter: (item: MappedEngineItem) => boolean,
  allowTransferForceServe: boolean,
] {
  return [excludeTransferProbes, false];
}

/** MA-768 — the review fetch's item filter. With no screen-supplied filter
 *  it is exactly the default (`reviewPoolCallArgs()[0]`, excludeTransferProbes).
 *  With one, it is "default AND screen filter", so a screen can narrow the
 *  review pool (ReadingSession: only items it can render) but can never let
 *  a transfer probe back in (MA-303b S5). Memoized per screen filter, so the
 *  result is referentially stable (MA-149 itemFilter contract) and a screen's
 *  pool-size gate can count with the very same function object. */
const composedReviewFilters = new WeakMap<
  (item: MappedEngineItem) => boolean,
  (item: MappedEngineItem) => boolean
>();
export function composeReviewItemFilter(
  screenFilter?: (item: MappedEngineItem) => boolean,
): (item: MappedEngineItem) => boolean {
  const [defaultFilter] = reviewPoolCallArgs();
  if (!screenFilter) return defaultFilter;
  let composed = composedReviewFilters.get(screenFilter);
  if (!composed) {
    composed = (item: MappedEngineItem) => defaultFilter(item) && screenFilter(item);
    composedReviewFilters.set(screenFilter, composed);
  }
  return composed;
}

/** MA-768 RED r2 — the review fetch's EXACT args, as a pure function the
 *  hook spreads from (MA-303b's reviewPoolCallArgs pattern: the hook cannot
 *  run in tests, so its arguments live here and are pinned here).
 *  `[composeReviewItemFilter(screenFilter), false]` — the review pool never
 *  force-serves, never carries a transfer probe, and is narrowed by the
 *  screen's filter when one is given. With no screen filter it equals
 *  reviewPoolCallArgs(). */
export function reviewFetchArgs(
  screenFilter?: (item: MappedEngineItem) => boolean,
): [itemFilter: (item: MappedEngineItem) => boolean, allowTransferForceServe: boolean] {
  const [, allowForce] = reviewPoolCallArgs();
  return [composeReviewItemFilter(screenFilter), allowForce];
}

export function useEngineQuestionsForRun(
  run: import("./progression").SessionRun,
  childId: string | null,
  audience: ContentAudience = "independent_practice",
  // ENGINE-VARIETY: per-session seed, forwarded to both pools.
  sessionSeed?: string,
  // MA-303b RED round 1 (R2/R6) — opt-in force-serve for the FOCAL fetch
  // ONLY, default false. R6: the review fetch below NEVER receives this
  // flag (hardcoded false) — RED found the review pool reachable through
  // this exact hook, and a forced probe chosen for the spaced_review slot
  // gets BOTH `isDelayedReview: true` (adaptiveReason === "spaced_review")
  // AND the transfer tags stamped on the SAME attempt, satisfying two
  // independent trueMastery conjuncts off one answer. MathSession is the
  // only caller that passes true (it is the only screen that tags —
  // transferTagsForItem's sole production call site).
  allowTransferForceServeForFocal = false,
  // MA-768 — optional screen-supplied filter that NARROWS the review fetch
  // (composed with the default via composeReviewItemFilter, so transfer
  // probes stay excluded whatever the screen passes). Must be module-level.
  // ReadingSession passes isReadingScreenRenderable; its review gate counts
  // with composeReviewItemFilter(isReadingScreenRenderable), the same object.
  // Omitted = the default review filter, every existing caller unchanged.
  // (While run.reviewSkillId is null the review fetch asks for the focal
  // skill; its rows are never used — the zip below only reads the review
  // pool for a real review skill — whatever the filter.)
  reviewScreenFilter?: (item: MappedEngineItem) => boolean,
): EngineQuestionsResult {
  const focalSkillId = run.focalSkillId;
  const reviewSkillId = run.reviewSkillId ?? focalSkillId;
  const sessionLength = run.questions.length;

  const focalResult = useEngineQuestions(focalSkillId, childId, sessionLength, audience, sessionSeed, undefined, allowTransferForceServeForFocal);
  // R6 (round 1) / S5 (round 2, RED r6.mjs) — review pool NEVER force-serves
  // (hardcoded false, independent of the caller's own opt-in) AND, more
  // fundamentally, never carries a transfer probe AT ALL — forced or not.
  // RED found the unforced path still open: ms.math.09 lands in a review
  // pool naturally (~14%, no force needed) and pickReviewItemForTier can
  // select it for a spaced_review slot, where a single correct answer
  // stamps BOTH isDelayedReview:true and the transfer tags on one attempt —
  // clearing both trueMastery conjuncts (transfer pass + cross-day delayed-
  // review pass) off one answer. Fixed at the pool-construction seam:
  // excludeTransferProbes filters isTransferProbe items out of the review
  // pool's unique pool BEFORE shuffle, so they can never reach
  // withBoundedRepetition or pickReviewItemForTier regardless of the force
  // gate. A second, independent belt lives on MathSession's submit path
  // (never stamps isDelayedReview on a transfer-tagged attempt).
  // MA-768: every review-fetch arg comes from the pinned pure reviewFetchArgs.
  const [reviewItemFilter, reviewAllowForce] = reviewFetchArgs(reviewScreenFilter);
  const reviewResult = useEngineQuestions(reviewSkillId, childId, sessionLength, audience, sessionSeed, reviewItemFilter, reviewAllowForce);

  // Zip per the run pattern. For each ordinal:
  //   - if the question is focal-strand → pull from focalResult.items
  //   - if the question's skillId === reviewSkillId AND review is real
  //     → pull from reviewResult.items
  //   - otherwise (cross_strand other-strand) → substitute with focal
  //     (T1.4c same-strand-only contract)
  // We count how many focal/review items we've already consumed and
  // index into the pool by that consumed count.
  //
  // #27: NO modulo wrap. Both pools are ALREADY bounded by
  // withBoundedRepetition (each unique item appears <= 2x), so wrapping
  // here would re-introduce the unbounded repetition the cap exists to
  // prevent. When a pool is exhausted (idx past its end) the slot is
  // dropped — the run shrinks to the bounded item count, and the screen's
  // effectiveLength = min(sessionLength, items.length) honors the shorter
  // run. For a well-resourced focal skill this loop is unchanged (the
  // pool already holds sessionLength items).
  let focalConsumed = 0;
  let reviewConsumed = 0;
  const items: MappedEngineItem[] = [];
  for (const q of run.questions) {
    const useReview =
      run.reviewSkillId !== null && q.skillId === run.reviewSkillId;
    const pool = useReview ? reviewResult.items : focalResult.items;
    const idx = useReview ? reviewConsumed : focalConsumed;
    const pulled = pool[idx];
    if (pulled) {
      items.push(pulled);
      if (useReview) reviewConsumed += 1;
      else focalConsumed += 1;
    }
  }

  return {
    items,
    focalPool: focalResult.items,
    reviewPool: reviewResult.items,
    // resolvedSkillId carries the focal walkback resolution for back-
    // compat with consumers that read it (e.g. recap analytics).
    resolvedSkillId: focalResult.resolvedSkillId,
    decision: focalResult.decision,
    priorAttempts: focalResult.priorAttempts ?? [],  // focal skill's prior attempts for sessionAttemptsRef seed
    loading: focalResult.loading || reviewResult.loading,
    error: focalResult.error ?? reviewResult.error,
  };
}
